import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  CheckCircle2,
  XCircle,
  ArrowRight,
  Calendar,
  MessagesSquare,
  Send,
  ShieldCheck,
  Sparkles,
  Database,
  Radar,
  Minus,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { SEOHead } from '@/components/SEOHead';
import { Button } from '@/components/ui/button';
import { DemoContactForm } from '@/components/sections/DemoContactForm';

const steps = [
  {
    icon: Radar,
    title: 'Qualifies from real signals',
    body: "NoQ5 reads the calls, email threads and meeting activity already flowing through your CRM to spot genuine buying intent — not a scraped list of cold contacts to spray.",
  },
  {
    icon: Calendar,
    title: "Books the meeting for the AE",
    body: "When a prospect is ready, NoQ5 books the meeting directly onto the right AE's calendar, respecting availability, timezones and routing rules.",
  },
  {
    icon: Send,
    title: 'Sends the calendar invite',
    body: "Prospect and AE both get a calendar invite automatically, with the relevant context attached. No scheduling back-and-forth to lose the moment.",
  },
  {
    icon: MessagesSquare,
    title: 'Drops a brief into Slack or email',
    body: "The AE walks in prepared: a concise brief — who, why now, deal context, open questions — lands in Slack or email before the call.",
  },
];

const features = [
  {
    icon: Database,
    title: 'Works your pipeline, not a cold list',
    body: "Acts on the intent signals already in HubSpot, Salesforce or Pipedrive, so meetings come from real warm opportunities — not a purchased database.",
  },
  {
    icon: CheckCircle2,
    title: 'One-click CRM accuracy, no data garbage',
    body: 'Volume AI SDRs are notorious for creating duplicate and dead records. NoQ5 keeps your CRM current at the source, so bookings and reporting run on clean data.',
  },
  {
    icon: Calendar,
    title: 'Calendar-native booking and invites',
    body: "Books meetings straight onto the AE's calendar and sends invites to both sides automatically — respecting availability, timezones and routing.",
  },
  {
    icon: MessagesSquare,
    title: 'AE briefs in Slack and email',
    body: 'Every booked meeting comes with a pre-call brief delivered where reps already work: who the prospect is, why now, deal context, and the open questions to close on.',
  },
  {
    icon: Sparkles,
    title: 'Evidence-based qualification',
    body: 'Meetings are qualified on real engagement — replies, meeting activity, multi-threading — not a rep gut feel or a cold-open guess.',
  },
  {
    icon: ShieldCheck,
    title: 'EU-hosted and GDPR-compliant',
    body: 'Built in Ireland and GDPR-first, NoQ5 anonymises sensitive data before prompting LLMs — avoiding the compliance risks that dog US-centric volume outbound tools.',
  },
];

type Cell = boolean | string;
const comparison: { row: string; noq5: Cell; elevenx: Cell; artisan: Cell }[] = [
  { row: 'Primary job', noq5: 'Book meetings + brief the AE', elevenx: 'High-volume cold outbound', artisan: 'High-volume cold email + LinkedIn' },
  { row: "Books meeting on AE's calendar", noq5: true, elevenx: 'Email booking only', artisan: false },
  { row: 'Sends calendar invite automatically', noq5: true, elevenx: false, artisan: false },
  { row: 'Pre-call brief to Slack / email', noq5: true, elevenx: false, artisan: false },
  { row: 'Keeps CRM accurate automatically', noq5: true, elevenx: 'Can create duplicate records', artisan: 'Can create duplicate records' },
  { row: 'Works without a cold-email blast', noq5: true, elevenx: false, artisan: false },
  { row: 'Domain-reputation risk', noq5: 'Low — no mass cold send', elevenx: 'High — 5k+ emails/day', artisan: 'High — volume-led' },
  { row: 'EU-hosted & GDPR-first', noq5: true, elevenx: 'US-centric', artisan: 'LinkedIn ban, Jan 2026' },
  { row: 'Typical price / month', noq5: 'On request', elevenx: '~$5,000+', artisan: '~$2,000+' },
];

const faqs = [
  {
    q: 'What is an AI SDR?',
    a: "An AI SDR (sales development rep) is software that autonomously handles top-of-funnel sales work. Most AI SDRs focus on high-volume cold outbound. NoQ5's autonomous SDR focuses on the handoff instead: it qualifies real pipeline signals, books the meeting on the AE's calendar, sends the calendar invite, and briefs the AE before the call.",
  },
  {
    q: "How is NoQ5's AI SDR different from 11x or Artisan?",
    a: '11x (Alice) and Artisan (Ava) are volume machines that blast thousands of cold emails and measure success in send count. NoQ5 works the pipeline you already have — it books meetings for the AE, sends the invite, and keeps your CRM accurate at the source instead of filling it with duplicate and dead records.',
  },
  {
    q: 'Do AI SDRs wreck your CRM data?',
    a: 'Volume AI SDRs are widely reported to create duplicate records, dead contacts and routing conflicts because they generate records autonomously at high volume. NoQ5 is built the other way round: it keeps HubSpot, Salesforce and Pipedrive accurate at the source, so every booking and brief runs on clean data.',
  },
  {
    q: 'Does the AI SDR book meetings and send calendar invites automatically?',
    a: "Yes. When a prospect is ready, NoQ5 books the meeting on the right AE's calendar — respecting availability and routing — and sends the calendar invite to both sides automatically. It then drops a concise pre-call brief into Slack or email so the AE walks in prepared.",
  },
  {
    q: "Is NoQ5's AI SDR GDPR-compliant?",
    a: 'Yes. NoQ5 is EU-hosted and GDPR-compliant, anonymising sensitive data before prompting LLMs — a genuine gap left by US-centric AI SDR tools, some of which have faced platform and compliance issues.',
  },
  {
    q: 'Does it work with HubSpot, Salesforce and Pipedrive?',
    a: 'Yes, natively. NoQ5 sits on top of your CRM, reads the signals already flowing through it, and writes approved updates back. It never becomes a parallel source of truth.',
  },
];

const CellView = ({ v, highlight }: { v: Cell; highlight?: boolean }) => {
  if (v === true) return <CheckCircle2 className={`w-5 h-5 mx-auto ${highlight ? 'text-primary' : 'text-emerald-500'}`} aria-label="Yes" />;
  if (v === false) return <Minus className="w-5 h-5 mx-auto text-muted-foreground/50" aria-label="No" />;
  return <span className={`text-sm ${highlight ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>{v}</span>;
};

const AiSdr = () => {
  const scrollToDemo = () => {
    document.getElementById('book-demo')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <Layout>
      <SEOHead
        title="AI SDR That Books Meetings for Your AEs | NoQ5"
        description="NoQ5's autonomous AI SDR books qualified meetings on the AE's calendar, sends the invite, and drops a pre-call brief into Slack or email. Works on top of HubSpot, Salesforce and Pipedrive. EU-hosted, GDPR-first."
        canonicalUrl="https://noq5.ai/ai-sdr"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://noq5.ai/' },
              { '@type': 'ListItem', position: 2, name: 'AI SDR', item: 'https://noq5.ai/ai-sdr' },
            ],
          },
          {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: faqs.map((f) => ({
              '@type': 'Question',
              name: f.q,
              acceptedAnswer: { '@type': 'Answer', text: f.a },
            })),
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: 'NoQ5 Autonomous AI SDR',
            description:
              "Autonomous AI SDR that books qualified meetings on the AE's calendar, sends the invite, and drops a pre-call brief into Slack or email — on top of HubSpot, Salesforce and Pipedrive.",
            brand: { '@type': 'Brand', name: 'NoQ5' },
            category: 'AI SDR Software',
          },
        ]}
      />

      {/* HERO */}
      <section className="relative w-full overflow-hidden bg-background">
        <div className="absolute inset-0 opacity-[0.03]" style={{
          backgroundImage: `linear-gradient(hsl(var(--foreground)) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--foreground)) 1px, transparent 1px)`,
          backgroundSize: '64px 64px'
        }} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[600px] rounded-full bg-primary/[0.05] blur-[100px]" />

        <div className="container relative z-10 px-4 md:px-6 py-20 md:py-28">
          <div className="max-w-5xl mx-auto">
            <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-primary/8 border border-primary/15 mb-8">
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span className="text-sm font-semibold text-foreground/80">AI SDR · Autonomous meeting booking · 2026</span>
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold leading-[1.05] mb-6">
              <span className="text-foreground">The AI SDR that books the meeting,</span>
              <br />
              <span className="text-gradient-teal">and briefs the AE before the call.</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-10 leading-relaxed">
              Most AI SDRs just blast cold email and measure success in send volume — then quietly fill your CRM with dead contacts and duplicate records. NoQ5's autonomous SDR works differently: it books qualified meetings straight onto the AE's calendar, sends the calendar invite, and drops a ready-to-run brief into Slack or email — on top of a CRM it keeps accurate automatically. EU-hosted, GDPR-first.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-12">
              <Button size="lg" onClick={scrollToDemo} className="bg-primary hover:bg-primary/90 text-primary-foreground font-bold px-8 py-6 text-lg rounded-xl shadow-button hover:shadow-glow transition-all duration-300 group">
                Book a Demo
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href="#how-it-works">
                <Button size="lg" variant="outline" className="font-bold px-8 py-6 text-lg rounded-xl border-2 border-border hover:bg-muted w-full">
                  See how it works
                </Button>
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { icon: Calendar, label: 'Books the meeting', desc: "Qualified meetings booked onto the AE's calendar" },
                { icon: Send, label: 'Sends the invite', desc: 'Calendar invites to both sides, no back-and-forth' },
                { icon: MessagesSquare, label: 'Briefs the AE', desc: 'A pre-call brief lands in Slack or email' },
              ].map((c) => (
                <div key={c.label} className="bg-card rounded-xl border border-border p-4 hover:border-primary/30 transition-colors">
                  <c.icon className="w-5 h-5 text-primary mb-2.5" />
                  <p className="text-sm font-bold text-foreground mb-1">{c.label}</p>
                  <p className="text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how-it-works" className="w-full py-20 md:py-28 bg-muted/30">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">How the NoQ5 AI SDR works</h2>
            <p className="text-lg text-muted-foreground">Not another cold-email cannon. NoQ5 turns the signals already in your CRM into booked meetings and prepared AEs — in four autonomous steps.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {steps.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="bg-card border border-border rounded-2xl p-6 md:p-8 shadow-subtle"
              >
                <div className="flex items-center gap-3 mb-4">
                  <span className="text-xs font-bold text-primary tracking-widest">0{i + 1}</span>
                  <div className="h-px flex-1 bg-border" />
                  <div className="p-2 rounded-lg bg-primary/10 text-primary">
                    <s.icon className="w-5 h-5" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-foreground mb-2">{s.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="w-full py-20 md:py-28">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">What makes it truly autonomous</h2>
            <p className="text-lg text-muted-foreground">Six things NoQ5's AI SDR does that volume outbound tools can't — because it lives on top of a CRM it keeps accurate.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 max-w-6xl mx-auto">
            {features.map((f) => (
              <div key={f.title} className="bg-card border border-border rounded-2xl p-6 hover:border-primary/30 hover:shadow-subtle transition-all">
                <div className="p-2.5 rounded-lg bg-primary/10 text-primary w-fit mb-4">
                  <f.icon className="w-5 h-5" />
                </div>
                <h3 className="text-base font-bold text-foreground mb-2">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="w-full py-20 md:py-28 bg-muted/30">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">NoQ5 vs volume AI SDRs</h2>
            <p className="text-lg text-muted-foreground">Most AI SDRs optimise for send volume. NoQ5 optimises for booked meetings and prepared AEs — on clean CRM data.</p>
          </div>

          <div className="max-w-5xl mx-auto overflow-x-auto">
            <table className="w-full border-collapse bg-card border border-border rounded-2xl overflow-hidden">
              <thead>
                <tr className="bg-muted/50">
                  <th className="text-left px-5 py-4 text-sm font-semibold text-muted-foreground">Capability</th>
                  <th className="px-5 py-4 text-sm font-bold text-primary bg-primary/5">
                    NoQ5
                    <div className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider mt-0.5">Autonomous SDR</div>
                  </th>
                  <th className="px-5 py-4 text-sm font-semibold text-foreground">
                    11x
                    <div className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider mt-0.5">Alice</div>
                  </th>
                  <th className="px-5 py-4 text-sm font-semibold text-foreground">
                    Artisan
                    <div className="text-[10px] font-normal text-muted-foreground uppercase tracking-wider mt-0.5">Ava</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparison.map((r, i) => (
                  <tr key={r.row} className={i % 2 === 0 ? 'bg-background' : 'bg-muted/20'}>
                    <td className="px-5 py-4 text-sm font-medium text-foreground">{r.row}</td>
                    <td className="px-5 py-4 text-center bg-primary/5"><CellView v={r.noq5} highlight /></td>
                    <td className="px-5 py-4 text-center"><CellView v={r.elevenx} /></td>
                    <td className="px-5 py-4 text-center"><CellView v={r.artisan} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-muted-foreground mt-4 max-w-3xl">
              Sources: 11x and Artisan pricing and capabilities per vendor pages and G2, with independent breakdowns from The RevOps Report, ConsultStack and Nexus (2026). Figures are estimates; request current quotes. See also our <Link to="/best-sales-forecasting-software-2026" className="text-primary hover:underline">sales forecasting guide</Link>.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="w-full py-20 md:py-28">
        <div className="container px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">AI SDR: FAQ</h2>
            <p className="text-lg text-muted-foreground">Common questions about AI SDRs and how NoQ5's autonomous SDR books meetings and briefs your AEs.</p>
          </div>

          <div className="mx-auto max-w-3xl space-y-4">
            {faqs.map((f, i) => (
              <motion.div
                key={f.q}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                viewport={{ once: true }}
                className="bg-card border border-border rounded-2xl p-6 shadow-subtle"
              >
                <h3 className="text-lg font-semibold mb-3 text-foreground">{f.q}</h3>
                <p className="text-muted-foreground leading-relaxed">{f.a}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <DemoContactForm />
    </Layout>
  );
};

export default AiSdr;
